/**
 * Stage → payload parser registry for ``checkpoint`` events.
 *
 * Adding a new checkpoint stage with stage-specific state means:
 *   1. Add a literal to ``EpiCheckpointStage``.
 *   2. Add a parser here (and export it if a test or sibling needs it).
 *   3. Add a card to ``CHECKPOINT_CARDS`` in ``MessageBubble``.
 *
 * Stages that don't carry a structured payload return ``null``.
 *
 * Kept as a sibling file under ``lib/api/`` rather than inline in
 * ``useEpiChatStream`` so the reducer file stays focused on event →
 * action dispatch and the parser surface can grow without bloating it.
 */

import type {
  CohortPlan,
  CohortPlanStub,
  ConceptMapping,
  ConceptMappingGroup,
  EpiCheckpointStage,
  InlineConcept,
  UnifiedIntentConceptGroup,
  UnifiedIntentPayload,
} from '../types/epi-chat-types';

const COHORT_PLAN_STUB_ROLES = ['index', 'inclusion', 'exclusion'] as const;

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const asNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const asStringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** Parse one concept group bound to a mapping (curated path). */
const parseConceptMappingGroup = (raw: unknown): ConceptMappingGroup | null => {
  if (!isRecord(raw)) return null;
  const name = asString(raw.name) ?? '';
  const conceptGroupId = asString(raw.concept_group_id) ?? asString(raw.conceptGroupId) ?? '';
  if (!name && !conceptGroupId) return null;
  return {
    conceptGroupId,
    name,
    owner: asString(raw.owner) ?? null,
    domains: asStringList(raw.domains),
  };
};

/** Parse one inline standard concept bound to a mapping (ad-hoc-id path). */
const parseInlineConcept = (raw: unknown): InlineConcept | null => {
  if (!isRecord(raw)) return null;
  const conceptId = asNumber(raw.concept_id) ?? asNumber(raw.conceptId);
  const name = asString(raw.name) ?? '';
  if (conceptId === undefined || !name) return null;
  return { conceptId, name, standard: raw.standard !== false };
};

/** Parse one structured concept-mapping row from the backend ``concept_mappings``. */
const parseConceptMapping = (raw: unknown, idx: number): ConceptMapping | null => {
  if (!isRecord(raw)) return null;
  const clinicalTerm = asString(raw.clinical_term) ?? asString(raw.clinicalTerm) ?? '';
  if (!clinicalTerm) return null;
  const type = asString(raw.type) === 'inline_concept' ? 'inline_concept' : 'concept_group';
  const groupsRaw = raw.concept_groups ?? raw.conceptGroups;
  const inlineRaw = raw.inline_concepts ?? raw.inlineConcepts;
  return {
    // clinicalTerm is non-empty here (guarded above), so it's a safe id fallback.
    mappingId: asString(raw.mapping_id) ?? asString(raw.mappingId) ?? clinicalTerm,
    clinicalTerm,
    table: asString(raw.table) ?? null,
    column: asString(raw.column) ?? null,
    domain: asString(raw.domain) ?? null,
    type,
    conceptGroups: Array.isArray(groupsRaw)
      ? groupsRaw.map(parseConceptMappingGroup).filter((g): g is ConceptMappingGroup => g !== null)
      : [],
    inlineConcepts: Array.isArray(inlineRaw)
      ? inlineRaw.map(parseInlineConcept).filter((c): c is InlineConcept => c !== null)
      : [],
    status: asString(raw.status) ?? null,
  };
};

/**
 * Parse the backend ``concept_mappings`` array (snake_case wire shape) into
 * structured ``ConceptMapping`` rows. Shared by the unified intent checkpoint
 * card (OR-6018) and the AV Definition tab's read-only "Concept mappings"
 * section (OR-6214) so both consume one shape. Non-array / bad entries → ``[]``.
 */
export const parseConceptMappings = (raw: unknown): ConceptMapping[] =>
  Array.isArray(raw)
    ? raw.map(parseConceptMapping).filter((m): m is ConceptMapping => m !== null)
    : [];

/** Project a legacy single-group row onto the structured concept-mapping shape. */
const legacyGroupToMapping = (g: UnifiedIntentConceptGroup, idx: number): ConceptMapping => ({
  mappingId: g.clinicalTerm || `mapping-${idx}`,
  clinicalTerm: g.clinicalTerm,
  table: null,
  column: null,
  domain: g.domainHint,
  type: 'concept_group',
  conceptGroups: g.name
    ? [{ conceptGroupId: g.conceptGroupId ?? '', name: g.name, owner: g.owner, domains: g.domains }]
    : [],
  inlineConcepts: [],
  status: g.status,
});

/**
 * Parse the unified intent checkpoint payload (OR-6018). Returns ``null`` for a
 * legacy intent checkpoint — the discriminator is a ``concept_mappings`` or
 * ``concept_groups`` array, which only the unified backend flow injects.
 * ``definition`` / ``format`` / ``usedIn`` are the agent's freeform proposal
 * (accepted as strings; a ``format`` object is reduced to its ``type``).
 *
 * ``conceptMappings`` is always populated — from the structured backend
 * ``concept_mappings`` when present, else synthesized from the legacy
 * ``concept_groups`` rows — so the card can consume one shape regardless of
 * which the backend emits. ``conceptGroups`` is retained for the current card.
 */
export const parseUnifiedIntent = (raw: unknown): UnifiedIntentPayload | null => {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;

  const mappingsRaw = obj.concept_mappings ?? obj.conceptMappings;
  const groupsRaw = obj.concept_groups ?? obj.conceptGroups;
  if (!Array.isArray(mappingsRaw) && !Array.isArray(groupsRaw)) return null;

  const conceptGroups: UnifiedIntentConceptGroup[] = (Array.isArray(groupsRaw) ? groupsRaw : [])
    .filter((g): g is Record<string, unknown> => typeof g === 'object' && g !== null)
    .map((g) => ({
      clinicalTerm: asString(g.clinical_term) ?? asString(g.clinicalTerm) ?? '',
      domainHint: asString(g.domain_hint) ?? asString(g.domainHint) ?? null,
      status: asString(g.status) ?? null,
      conceptGroupId: asString(g.concept_group_id) ?? asString(g.conceptGroupId) ?? null,
      name: asString(g.name) ?? null,
      owner: asString(g.owner) ?? null,
      domains: asStringList(g.domains),
    }))
    .filter((g) => g.clinicalTerm.length > 0);

  const conceptMappings: ConceptMapping[] = Array.isArray(mappingsRaw)
    ? mappingsRaw.map(parseConceptMapping).filter((m): m is ConceptMapping => m !== null)
    : conceptGroups.map(legacyGroupToMapping);

  return {
    definition: asString(obj.definition) ?? null,
    format: coerceFormatText(obj.format),
    usedIn: asString(obj.used_in) ?? asString(obj.usedIn) ?? null,
    conceptGroups,
    conceptMappings,
  };
};

/**
 * ``format`` may arrive as a plain string OR a structured object the agent
 * emits — e.g. ``{output_type, values}`` or ``{type}``. Reduce any object to a
 * single readable line (preferring the human-readable ``values``) so it renders
 * as prose and never reaches ``<Markdown>`` as a non-string, which throws
 * (``.replace is not a function``) and takes down the card. Returns ``null``
 * when there's nothing usable. Exported so the card can apply the same coercion
 * on replay/cast paths that bypass {@link parseUnifiedIntent}.
 */
export const coerceFormatText = (value: unknown): string | null => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const o = value as Record<string, unknown>;
    return (
      asString(o.values) ??
      asString(o.type) ??
      asString(o.output_type) ??
      asString(o.outputType) ??
      null
    );
  }
  return null;
};

export const parseCohortPlan = (raw: unknown): CohortPlan | null => {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;

  const stubsRaw = obj.stubs;
  if (!Array.isArray(stubsRaw)) return null;
  const stubs = stubsRaw
    .filter(
      (entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null
    )
    .map((entry) => ({
      role: asString(entry.role) ?? '',
      description: asString(entry.description) ?? '',
    }))
    .filter(
      (entry): entry is CohortPlanStub =>
        (COHORT_PLAN_STUB_ROLES as readonly string[]).includes(entry.role) &&
        entry.description.length > 0
    );

  const studyPeriodRaw = obj.study_period ?? obj.studyPeriod;
  const studyPeriod =
    studyPeriodRaw && typeof studyPeriodRaw === 'object'
      ? {
          startDate:
            asString((studyPeriodRaw as Record<string, unknown>).start_date) ??
            asString((studyPeriodRaw as Record<string, unknown>).startDate) ??
            null,
          endDate:
            asString((studyPeriodRaw as Record<string, unknown>).end_date) ??
            asString((studyPeriodRaw as Record<string, unknown>).endDate) ??
            null,
          description: asString((studyPeriodRaw as Record<string, unknown>).description) ?? null,
        }
      : null;

  return {
    cohortName: asString(obj.cohort_name) ?? asString(obj.cohortName) ?? '',
    cohortDescription: asString(obj.cohort_description) ?? asString(obj.cohortDescription) ?? '',
    studyPeriod,
    stubs,
  };
};

export const CHECKPOINT_PAYLOAD_PARSERS: Record<EpiCheckpointStage, (raw: unknown) => unknown> = {
  intent: parseUnifiedIntent,
  bindings: () => null,
  draft: () => null,
  validation: () => null,
  judge: () => null,
  cohort_plan: parseCohortPlan,
  // OR-6271: concept-group create consent carries no structured payload — the
  // checkpoint summary is the yes/no question.
  concept_group: () => null,
  // The analysis processing gate carries no structured payload.
  process_analysis: () => null,
};
